import type { PartnerSyncPlan, ReportRenderOutcome, SyncOutcome } from '../types/partners';

/**
 * Turning what Mr. Roboto did (or could not do) after a write into the one
 * inline notice staff read. The write is always saved before the bot is
 * poked, so even the worst outcome here starts from "saved".
 */
export interface PartnerNotice {
  tone: 'success' | 'info' | 'warning' | 'danger';
  text: string;
  /** Somewhere to go next, e.g. the partner page after linking a channel from the admin tab. */
  link?: { to: string; label: string };
}

/** The bot's `errors` entries are free-form; show whatever sentence they carry. */
export function errorText(entry: unknown): string {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    for (const key of ['error', 'message', 'detail', 'description']) {
      if (typeof record[key] === 'string' && record[key]) return record[key] as string;
    }
  }
  try {
    return JSON.stringify(entry);
  } catch {
    return String(entry);
  }
}

const sentence = (text: string): string => {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * @param saved What was saved, as a sentence ("Added Alice to the team.").
 * @param managed Whether Discord is managed for this partner. When it is not,
 *   the backend does not poke the bot at all and `outcome` is null.
 */
export function describeSyncOutcome(outcome: SyncOutcome | null | undefined, saved: string, managed = true): PartnerNotice {
  const lead = sentence(saved);
  if (!outcome) {
    return {
      tone: 'success',
      text: managed ? lead : `${lead} Discord is not managed for this partner yet, so nothing changes there.`,
    };
  }
  if (!outcome.ok) {
    const reason = sentence(outcome.error || 'Mr. Roboto could not be reached.');
    return {
      tone: 'warning',
      text: /saved/i.test(reason) ? `${lead} ${reason}` : `${lead} ${reason} The change is saved and will apply on Mr. Roboto’s next pass.`,
    };
  }

  const result = outcome.result;
  const parts = [lead];
  const events = (result.observed?.events ?? []).map((e) => e.description?.trim()).filter(Boolean) as string[];
  if (result.dry_run) {
    parts.push('That was a preview, so nothing changed in Discord.');
  } else if (events.length > 0) {
    parts.push(`Mr. Roboto ${events.length === 1 ? 'did this' : 'did these'}: ${events.map((e) => e.replace(/[.]$/, '')).join('; ')}.`);
  } else if (result.applied) {
    parts.push('Discord already matched, so Mr. Roboto changed nothing.');
  }

  const held = result.plan?.roles?.held ?? [];
  if (held.length > 0) {
    parts.push(`${plural(held.length, 'role removal is', 'role removals are')} held back for now. ${held[0].reason ? sentence(held[0].reason) : ''}`.trim());
  }

  const errors = (result.errors ?? []).map(errorText).filter(Boolean);
  if (errors.length > 0) {
    parts.push(`Mr. Roboto reported ${plural(errors.length, 'problem', 'problems')}: ${errors.map((e) => e.replace(/[.]$/, '')).join('; ')}.`);
  }

  return { tone: errors.length > 0 ? 'warning' : held.length > 0 ? 'info' : 'success', text: parts.join(' ') };
}

// ── Plans (what a sync or preview would do) ────────────────────────────────

/** Turns a Discord ID into the name staff know them by. */
export type NameOf = (discordId: string) => string;

const ROLE_NAMES: Record<string, string> = { developer: 'Partnered Developer', contributor: 'Contributor' };
const roleName = (role: string) => ROLE_NAMES[role] ?? role;

/** "Alice, Bob and Carol", or "Alice, Bob, Carol and 4 more" past `max`. */
export function listNames(ids: string[], nameOf: NameOf, max = 4): string {
  const names = ids.map(nameOf);
  if (names.length <= 1) return names[0] ?? '';
  if (names.length > max) return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const joinAnd = (parts: string[]) =>
  parts.length <= 1 ? parts[0] ?? '' : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * The channel name Mr. Roboto gives a new partner channel, mirroring its
 * `channel_slug`: lowercase, spaces and underscores to hyphens, `[a-z0-9-]`
 * only, the words other automation reacts to stripped, at most 90 characters,
 * `partner` if nothing is left. Only used to label a preview.
 */
const SLUG_TRIGGER_WORDS = ['compensation', 'bug-report', 'bugreport', 'serverissue', 'serverdown'];
export function channelSlug(name: string): string {
  let slug = (name ?? '').toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '');
  for (;;) {
    let stripped = slug;
    for (const word of SLUG_TRIGGER_WORDS) stripped = stripped.split(word).join('-');
    stripped = stripped.replace(/-{2,}/g, '-');
    if (stripped === slug) break;
    slug = stripped;
  }
  slug = slug.replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
  slug = slug.slice(0, 90).replace(/^-+|-+$/g, '');
  return slug || 'partner';
}

export function planChangeCount(plan: PartnerSyncPlan | null | undefined): number {
  if (!plan) return 0;
  return (
    (plan.channel?.action && plan.channel.action !== 'none' ? 1 : 0) +
    (plan.overwrites?.add.length ?? 0) +
    (plan.overwrites?.remove.length ?? 0) +
    (plan.roles?.add.length ?? 0) +
    (plan.roles?.remove.length ?? 0) +
    (plan.roles?.held.length ?? 0)
  );
}

/**
 * One sentence for the whole plan, e.g. "Will create #atm-team in
 * Partnerships, give 3 people channel access and grant 3 roles."
 */
export function planHeadline(plan: PartnerSyncPlan, channelName?: string | null): string {
  const chan = channelName ? `#${channelName}` : null;
  const cat = plan.channel.category_name;
  const parts: string[] = [];
  switch (plan.channel.action) {
    case 'create':
      parts.push(`create ${chan ?? 'a private channel'}${cat ? ` in ${cat}` : ''}`);
      break;
    case 'move':
      parts.push(`move ${chan ?? 'the channel'}${cat ? ` to ${cat}` : ''}`);
      break;
    case 'archive':
      parts.push(`archive ${chan ?? 'the channel'}`);
      break;
    case 'restore':
      parts.push(`restore ${chan ?? 'the channel'}${cat ? ` to ${cat}` : ''}`);
      break;
    default:
      break;
  }
  const { add, remove } = plan.overwrites;
  if (add.length) parts.push(`give ${plural(add.length, 'person', 'people')} channel access`);
  if (remove.length) parts.push(`take channel access from ${plural(remove.length, 'person', 'people')}`);
  if (plan.roles.add.length) parts.push(`grant ${plural(plan.roles.add.length, 'role', 'roles')}`);
  if (plan.roles.remove.length) parts.push(`remove ${plural(plan.roles.remove.length, 'role', 'roles')}`);
  if (plan.roles.held.length) parts.push(`hold back ${plural(plan.roles.held.length, 'role removal', 'role removals')}`);
  if (parts.length === 0) return 'Nothing to change: Discord already matches the team.';
  return `Will ${joinAnd(parts)}.`;
}

function groupByRole<T extends { discord_id: string; role: string }>(rows: T[]): Map<string, string[]> {
  const byRole = new Map<string, string[]>();
  for (const row of rows) byRole.set(row.role, [...(byRole.get(row.role) ?? []), row.discord_id]);
  return byRole;
}

/** The plan step by step, naming people, for the preview and the enable confirmation. */
export function planLines(plan: PartnerSyncPlan, nameOf: NameOf, channelName?: string | null): string[] {
  const chan = channelName ? `#${channelName}` : 'the channel';
  const cat = plan.channel.category_name;
  const lines: string[] = [];
  switch (plan.channel.action) {
    case 'create':
      lines.push(`Create ${channelName ? `#${channelName}` : 'a private channel'} in ${cat ?? 'the first partner category with room'}. Only the team and staff can see it.`);
      break;
    case 'move':
      lines.push(`Move ${chan} to ${cat ?? 'another partner category'}.`);
      break;
    case 'archive':
      lines.push(`Archive ${chan}: move it to ${cat ?? 'the staff-only archive category'}, so the team no longer sees it.`);
      break;
    case 'restore':
      lines.push(`Restore ${chan} to ${cat ?? 'the partner category'} and give the team access again.`);
      break;
    default:
      break;
  }
  if (plan.channel.detail) lines.push(sentence(plan.channel.detail));
  if (plan.overwrites.add.length) lines.push(`Give channel access to ${listNames(plan.overwrites.add, nameOf)}.`);
  if (plan.overwrites.remove.length) lines.push(`Take channel access from ${listNames(plan.overwrites.remove, nameOf)}.`);
  for (const [role, ids] of groupByRole(plan.roles.add)) lines.push(`Grant the ${roleName(role)} role to ${listNames(ids, nameOf)}.`);
  for (const [role, ids] of groupByRole(plan.roles.remove)) lines.push(`Remove the ${roleName(role)} role from ${listNames(ids, nameOf)}.`);
  const heldByReason = new Map<string, { discord_id: string; role: string }[]>();
  for (const held of plan.roles.held) heldByReason.set(held.reason, [...(heldByReason.get(held.reason) ?? []), held]);
  for (const [reason, rows] of heldByReason) {
    lines.push(`Hold back removing roles from ${listNames(rows.map((r) => r.discord_id), nameOf)}${reason ? `: ${reason.replace(/[.]$/, '')}` : ''}.`);
  }
  return lines;
}

// ── Report cards ───────────────────────────────────────────────────────────

/**
 * What happened to a report's Discord cards after a change: the partner's
 * channel card and the staff feed copy. Like a sync, the change is saved
 * first; the bot also picks up changed reports within a minute by itself.
 */
export function describeReportOutcome(outcome: ReportRenderOutcome | null | undefined, saved: string): PartnerNotice {
  const lead = sentence(saved);
  if (!outcome) return { tone: 'success', text: lead };
  if (!outcome.ok) {
    const reason = sentence(outcome.error || 'Mr. Roboto could not be reached.');
    return {
      tone: 'warning',
      text: /saved/i.test(reason) ? `${lead} ${reason}` : `${lead} ${reason} The report is saved, and Mr. Roboto updates its cards within a minute of being back.`,
    };
  }
  const rendered = outcome.result.rendered ?? [];
  const partner = rendered.some((r) => r.partner_message);
  const feed = rendered.some((r) => r.feed_message);
  const parts = [lead];
  if (partner && feed) parts.push('The card in the partner’s channel and the staff feed copy are up to date.');
  else if (partner) parts.push('The card in the partner’s channel is up to date.');
  else if (feed) parts.push('The staff feed copy is up to date.');
  const errors = (outcome.result.errors ?? []).map(errorText).filter(Boolean);
  if (errors.length) parts.push(`Mr. Roboto reported ${plural(errors.length, 'problem', 'problems')}: ${errors.map((e) => e.replace(/[.]$/, '')).join('; ')}.`);
  return { tone: errors.length ? 'warning' : 'success', text: parts.join(' ') };
}
